import { useTranslation } from '@thoughtspot/i18n';
import { jsonConstants } from '@thoughtspot/json-constants';
import { Alert, BannerAlertType, } from '@thoughtspot/radiant-react/widgets/alert';
import { Button } from '@thoughtspot/radiant-react/widgets/button';
import { Checkbox, useCheckboxState, } from '@thoughtspot/radiant-react/widgets/checkbox';
import { Divider } from '@thoughtspot/radiant-react/widgets/divider';
import { IconID } from '@thoughtspot/radiant-react/widgets/icon';
import { Input } from '@thoughtspot/radiant-react/widgets/input';
import { Horizontal, Vertical, View, } from '@thoughtspot/radiant-react/widgets/layout';
import { LoadingIndicator } from '@thoughtspot/radiant-react/widgets/loading-indicator';
import { Modal, ModalSize } from '@thoughtspot/radiant-react/widgets/modal';
import { SearchBar } from '@thoughtspot/radiant-react/widgets/search-bar';
import { TextArea } from '@thoughtspot/radiant-react/widgets/text-area';
import { Toggle } from '@thoughtspot/radiant-react/widgets/toggle';
import { Colors, Typography, } from '@thoughtspot/radiant-react/widgets/typography';
import { getAdminGroupGuid, getUserEmail, getUserGuid, isOrgUiEnabled, isWelcomeEmailEnabled, } from '@thoughtspot/session-service';
import { isOktaEnabled } from '@thoughtspot/system-config-service';
import cx from 'classnames';
import _ from 'lodash';
import React, { createRef, Fragment, useCallback, useEffect, useRef, useState, } from 'react';
import { getLabelWithOrWithOutErrorIcon, SHARING_VISIBILITY } from '../../util';
import { COUNT_LABELS, createEditTabLinkLabels, getEmailInputLabel, getInputLabel, getPasswordInputLabel, getSelectedCount, getWhitelistedDomainsText, hasCreateEditInputError, hasImplicitAdminAccess, isFieldDisabledForMultipleOrgs, ON_CHANGE_DELAY, TAB_LABEL_ID, TranslationsKeys, } from '../util';
import styles from './create-edit-modal.module.scss';
import { PasswordRequirements } from './password-requirements.container';
const FORM_INPUT_ITEM = {
    displayName: 'displayName',
    name: 'name',
    allowOthersToShare: 'allowOthersToShare',
    email: 'email',
    password: 'password',
    confirmPassword: 'confirmPassword',
};
const initialWarningDialogue = {
    isOpen: false,
    title: '',
    description: '',
};
const INITIAL_PASSWORD_CONFIG = {
    password: false,
    confirmPassword: false,
};
export const CreateEditForm = ({ userData, message, isEditMode, isModalOpened, setShowError, closeModal, onSuccess, groupListIds = [], orgListIds = [], preSelectedGroupListIds = [], preSelectedOrgListIds = [], userGroupList = [], orgList = [], filteredUserGroupList = [], filteredOrgList = [], filterListData, onSendWelcomeEmail, sendWelcomeEmail, setSendWelcomeEmail, setUserGroupsListInput, setOrgsListInput, areUserAndGroupsLoading, isOrgListLoading, currentOrgId, selectedGroupsRef, selectedOrgsRef, }) => {
    const { t } = useTranslation();
    const getTranslations = (key, args) => t(`${key}`, args);
    const ignoredSelections = useRef(new Set());
    const formData = useRef({
        name: '',
        displayName: '',
        allowOthersToShare: true,
        email: '',
    });
    const [isError, setIsError] = useState(false);
    const [errorMessage, setErrorMessage] = useState(message);
    const [warningDialogue, setWarningDialogue] = useState({
        ...initialWarningDialogue,
    });
    const [passwordConfig, setPasswordConfig] = useState(INITIAL_PASSWORD_CONFIG);
    const [isOrgNotAssociated, setIsOrgNotAssociated] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const isUserFederated = () => {
        return (userData?.getUserDetailById[0]?.userContent?.oidcUserType ===
            jsonConstants.FEDERATED_USER);
    };
    const isAllOrgContext = () => {
        return isOrgUiEnabled() && currentOrgId === -1;
    };
    const isCurrentUser = isEditMode && userData?.getUserDetailById?.[0]?.id === getUserGuid();
    const [form, setForm] = useState({
        name: userData?.getUserDetailById[0].header.name || '',
        displayName: userData?.getUserDetailById[0].header.displayName || '',
        allowOthersToShare: _.isUndefined(userData?.getUserDetailById[0])
            ? true
            : userData?.getUserDetailById[0].visibility ===
                SHARING_VISIBILITY.DEFAULT,
        email: userData?.getUserDetailById[0].userContent?.userProperties?.mail ||
            '',
        password: '',
        confirmPassword: '',
    });
    const [hasInputError, setHasInputError] = useState({
        isNameRequired: false,
        isDisplayNameRequired: false,
        isNameLong: false,
        isDisplayNameLong: false,
        isNotWhitelistedDomainEmail: false,
        isEmailEmpty: false,
        arePasswordsNotMatching: false,
        isPasswordEmpty: false,
        isConfirmPasswordEmpty: false,
        isPasswordValidationRuleFailed: false,
        isNameEmailNotSame: false,
    });
    const [untouchedFields, setUntouchedFields] = useState({
        isNameUntouched: true,
        isDisplayNameUntouched: true,
        isPasswordUntouched: true,
        isConfirmPasswordUntouched: true,
        isEmailUntouched: true,
    });
    const [emailContent, setEmailContent] = useState({
        sendEmail: true,
        testSendEmail: true,
        message: '',
        emailSent: false,
    });
    const isTestEmailLabelEnabled = !!getUserEmail();
    const { checkedItems: checkedGroupItems, handleChange: handleGroupChange, setCheckedItems: setCheckedGroupItems, } = useCheckboxState(groupListIds, preSelectedGroupListIds);
    const { checkedItems: checkedOrgItems, handleChange: handleOrgChange, setCheckedItems: setCheckedOrgItems, } = useCheckboxState(orgListIds, preSelectedOrgListIds);
    const componentRefs = useRef([]);
    const generateRefs = useCallback(() => {
        const count = createEditTabLinkLabels(isEditMode, currentOrgId).length;
        componentRefs.current = Array(count)
            .fill(null)
            .map((_, i) => componentRefs.current[i] ?? createRef());
    }, []);
    const onConfirmWarningDialogue = useCallback(() => {
        setWarningDialogue({ ...initialWarningDialogue });
    }, [warningDialogue]);
    const handleInputChange = _.debounce((event) => {
        const { id, value } = event.target;
        if (FORM_INPUT_ITEM.name === id) {
            setUntouchedFields({
                ...untouchedFields,
                isNameUntouched: false,
            });
            setForm({
                ...form,
                name: value.trim(),
            });
        }
        if (FORM_INPUT_ITEM.displayName === id) {
            setUntouchedFields({
                ...untouchedFields,
                isDisplayNameUntouched: false,
            });
            setForm({
                ...form,
                displayName: value.trim(),
            });
        }
        if (FORM_INPUT_ITEM.email === id) {
            setUntouchedFields({
                ...untouchedFields,
                isEmailUntouched: false,
            });
            setForm({
                ...form,
                email: value,
            });
        }
        if (FORM_INPUT_ITEM.password === id) {
            setUntouchedFields({
                ...untouchedFields,
                isPasswordUntouched: false,
            });
            setForm({
                ...form,
                password: value,
            });
        }
        if (FORM_INPUT_ITEM.confirmPassword === id) {
            setUntouchedFields({
                ...untouchedFields,
                isConfirmPasswordUntouched: false,
            });
            setForm({
                ...form,
                confirmPassword: value,
            });
        }
        if (id === 'sendWelcomeEmail') {
            setEmailContent({
                ...emailContent,
                message: value,
            });
        }
    }, ON_CHANGE_DELAY);
    const onChangePasswordSuffixIconClick = useCallback(() => {
        setPasswordConfig({
            ...passwordConfig,
            password: !passwordConfig.password,
        });
    }, [passwordConfig]);
    const onConfirmPasswordSuffixIconClick = useCallback(() => {
        setPasswordConfig({
            ...passwordConfig,
            confirmPassword: !passwordConfig.confirmPassword,
        });
    }, [passwordConfig]);
    const handleCheckboxChange = (e, id) => {
        if (FORM_INPUT_ITEM.allowOthersToShare === id) {
            setForm({
                ...form,
                allowOthersToShare: !form.allowOthersToShare,
            });
        }
        if (id === 'sendWelcomeEmailCheckbox') {
            setSendWelcomeEmail(!sendWelcomeEmail);
        }
    };
    useEffect(() => {
        if (!isError && !_.isEmpty(message)) {
            setIsError(true);
            setErrorMessage(message);
        }
    }, [message, isError]);
    useEffect(() => {
        setHasInputError(hasCreateEditInputError(hasInputError, form, isEditMode, untouchedFields));
    }, [form]);
    const onBannerClose = useCallback(() => {
        setShowError(null);
        setIsError(false);
        setErrorMessage(null);
    }, []);
    const submitForm = useCallback(() => {
        onBannerClose();
        const { isNameRequired, isDisplayNameRequired, isNameLong, isDisplayNameLong, isNotWhitelistedDomainEmail, arePasswordsNotMatching, isPasswordEmpty, isConfirmPasswordEmpty, isPasswordValidationRuleFailed, isEmailEmpty, } = hasCreateEditInputError(hasInputError, form, isEditMode, untouchedFields);
        if (isNameRequired ||
            isNameLong ||
            isDisplayNameRequired ||
            isDisplayNameLong ||
            isNotWhitelistedDomainEmail ||
            arePasswordsNotMatching ||
            isPasswordEmpty ||
            isConfirmPasswordEmpty ||
            isPasswordValidationRuleFailed ||
            isEmailEmpty) {
            return;
        }
        if (isOrgUiEnabled() &&
            currentOrgId === -1 &&
            checkedOrgItems.size === 0) {
            return;
        }
        const newFormData = {};
        const { name, displayName, email, password, allowOthersToShare } = form;
        if (isEditMode) {
            const { id, userContent: { userPreferences, userProperties, userActivityProto, }, state, inheritedGroups, privileges, type, parenttype, tenantId, header, complete, incompleteDetail, isSuperUser, isSystemPrincipal, } = userData?.getUserDetailById[0];
            if (!userPreferences.preferredLocale ||
                userPreferences.preferredLocale === null) {
                delete userPreferences.preferredLocale;
            }
            let isPersonaPresent = true;
            if (!userProperties.persona || userProperties.persona === null) {
                delete userProperties.persona;
                isPersonaPresent = false;
            }
            newFormData.password = password;
            newFormData.userid = id;
            const adminGroupId = getAdminGroupGuid();
            const isCurrentUser = id === getUserGuid();
            const wasOriginallyInAdminGroup = adminGroupId
                ? userData?.listUserGroupById?.some((g) => g.id === adminGroupId)
                : false;
            const assignedGroups = [...checkedGroupItems].filter(groupId => {
                if (groupId === adminGroupId &&
                    hasImplicitAdminAccess(isSuperUser, isCurrentUser) &&
                    !wasOriginallyInAdminGroup) {
                    return false;
                }
                return true;
            });
            newFormData.content = {
                userContent: {
                    userPreferences,
                    userProperties: {
                        mail: email,
                        ...(userProperties.displayNameLastUpdatedBy !== null
                            ? {
                                displayNameLastUpdatedBy: userProperties.displayNameLastUpdatedBy,
                            }
                            : {}),
                        frontendURL: window.location.hostname,
                    },
                    userActivityProto,
                },
                state,
                assignedGroups,
                inheritedGroups,
                privileges,
                type,
                parenttype,
                visibility: allowOthersToShare
                    ? SHARING_VISIBILITY.DEFAULT
                    : SHARING_VISIBILITY.NON_SHARABLE,
                tenantId,
                displayName,
                header: {
                    id,
                    indexVersion: header.indexVersion,
                    generationNum: header.generationNum,
                    name,
                    displayName,
                    description: header.description,
                    author: header.author,
                    created: header.created,
                    modified: header.modified,
                    modifiedBy: header.modifiedBy,
                    owner: header.owner,
                    isDeleted: header.isDeleted,
                    isHidden: header.isHidden,
                    orgIds: header.orgIds,
                    tags: header.tags,
                    type: header.type,
                    aiAnswerGenerationDisabled: header.aiAnswerGenerationDisabled,
                    aiAnswerState: header.aiAnswerState,
                    isExternal: header.isExternal,
                    isDeprecated: header.isDeprecated,
                    isSharedViaConnection: header.isSharedViaConnection,
                    contentUpgradeId: header.contentUpgradeId,
                    headerUpgradeId: header.headerUpgradeId,
                },
                complete,
                incompleteDetail,
                isSuperUser,
                isSystemPrincipal,
            };
            if (isAllOrgContext()) {
                newFormData.content.assignedGroups =
                    userData?.getUserDetailById?.[0]?.assignedGroups ?? [];
            }
            if (isPersonaPresent) {
                newFormData.content.userContent.userProperties = {
                    ...newFormData.content.userContent.userProperties,
                    persona: userProperties.persona,
                };
            }
            if (isOrgUiEnabled()) {
                if (isAllOrgContext()) {
                    newFormData.content.header = {
                        ...newFormData.content.header,
                        orgIds: [...checkedOrgItems],
                    };
                }
                newFormData.content.header = {
                    ...newFormData.content.header,
                    belongToAllOrgs: header.belongToAllOrgs,
                };
            }
            newFormData.triggeredbyadmin = true;
        }
        else {
            newFormData.name = name;
            newFormData.displayname = displayName;
            if (password) {
                newFormData.password = password;
            }
            newFormData.visibility = allowOthersToShare
                ? SHARING_VISIBILITY.DEFAULT
                : SHARING_VISIBILITY.NON_SHARABLE;
            newFormData.groups = [...checkedGroupItems];
            newFormData.properties = {
                mail: email,
                frontendURL: window.location.hostname,
            };
            if (isAllOrgContext()) {
                newFormData.orgids = [...checkedOrgItems];
            }
        }
        newFormData.emailMessage = emailContent.message;
        ignoredSelections.current.clear();
        onSuccess(newFormData)
            .then(() => {
            setIsLoading(false);
            return true;
        })
            .catch((e) => {
            setIsLoading(false);
            setIsError(true);
            return e;
        });
    }, [
        form,
        checkedGroupItems,
        checkedOrgItems,
        emailContent,
        hasInputError,
        sendWelcomeEmail,
    ]);
    const onDismissModal = () => {
        ignoredSelections.current.clear();
        closeModal();
    };
    const onConfirmModal = () => {
        if (isLoading)
            return;
        const updatedUntouchedFields = {
            isNameUntouched: false,
            isDisplayNameUntouched: false,
            isPasswordUntouched: false,
            isConfirmPasswordUntouched: false,
            isEmailUntouched: false,
        };
        setUntouchedFields(updatedUntouchedFields);
        formData.current = {
            name: '',
            displayName: '',
            allowOthersToShare: true,
            email: '',
        };
        const { name, displayName, allowOthersToShare, email, password, confirmPassword, } = form;
        if (name) {
            formData.current.name = name;
        }
        if (displayName) {
            formData.current.displayName = displayName;
        }
        if (email) {
            formData.current.email = email;
        }
        formData.current.allowOthersToShare = allowOthersToShare;
        if (!name ||
            !displayName ||
            !email ||
            (!isOktaEnabled() && !isEditMode && !password) ||
            (!isOktaEnabled() && password !== confirmPassword)) {
            setHasInputError(hasCreateEditInputError(hasInputError, form, isEditMode, updatedUntouchedFields));
            componentRefs.current[0].current.scrollIntoView({
                behavior: 'smooth',
            });
            return;
        }
        if (isOrgUiEnabled() &&
            currentOrgId === -1 &&
            checkedOrgItems.size === 0) {
            setIsOrgNotAssociated(true);
            componentRefs.current[1].current.scrollIntoView({
                behavior: 'smooth',
            });
            return;
        }
        if (isError || !_.isEmpty(errorMessage)) {
            setIsError(false);
            setErrorMessage(null);
        }
        setIsLoading(true);
        submitForm();
    };
    const modalProps = {
        size: ModalSize.MEDIUM,
        title: isEditMode
            ? t(TranslationsKeys.EDIT_MODAL_TITLE)
            : t(TranslationsKeys.CREATE_MODAL_TITLE),
        onDismiss: onDismissModal,
        isOpen: isModalOpened,
        confirmButton: {
            text: t(isEditMode ? TranslationsKeys.SAVE : TranslationsKeys.ADD),
            onConfirm: onConfirmModal,
            disabled: isLoading,
        },
        dismissButton: {
            text: t(TranslationsKeys.CANCEL),
            disabled: isLoading,
        },
        className: styles.modalProps,
        contentClassName: styles.modalContentClassName,
        customHeaderClassName: styles.customHeader,
        customFooterClassName: styles.customFooter,
        autoScrollContent: false,
    };
    const renderTabLinks = (componentRefs, records) => {
        const scrollToComponent = (ref) => {
            ref.current.scrollIntoView({ behavior: 'smooth' });
        };
        return (React.createElement(Vertical, { className: styles.leftPanel, role: "navigation" }, componentRefs.map((ref, index) => (React.createElement(Horizontal, { key: records[index], className: styles.leftPanelHeader, vAlignContent: "center" },
            React.createElement("button", { onClick: () => scrollToComponent(ref), onKeyDown: e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        scrollToComponent(ref);
                    }
                }, type: "button", className: styles.leftPanelButton, "data-testid": `headerLink-${records[index]}` },
                React.createElement(Typography, { variant: "p", overrideTag: "span" }, getTranslations(`users.modalTabLinkLabel.${records[index]}`))))))));
    };
    const showSelectedByGroup = (checked, data, checkedItems, tab) => {
        const filterChecked = data.filter((item) => {
            const id = isAllOrgContext() ? item.orgId : item.id;
            return checkedItems.has(id);
        });
        if (!filterChecked.length || data.length === filterChecked.length) {
            return;
        }
        const updatedData = checked ? [...filterChecked] : [...data];
        filterListData(tab, updatedData);
    };
    const selectAllOrClearByGroup = (data, isClear, setCheckedItems, checkedItems, tab) => {
        const objRef = {
            [TAB_LABEL_ID.SELECT_GROUPS]: selectedGroupsRef,
            [TAB_LABEL_ID.SELECT_ORGS]: selectedOrgsRef,
        };
        const selectableData = data.filter((record) => {
            const id = isAllOrgContext() ? record.orgId : record.id;
            return !ignoredSelections.current.has(id);
        });
        if (isOrgNotAssociated && isAllOrgContext()) {
            setIsOrgNotAssociated(false);
        }
        if (isClear) {
            const filteredData = selectableData.filter((item) => {
                const id = isAllOrgContext() ? item.orgId : item.id;
                return checkedItems.has(id);
            });
            if (filteredData.length !== selectableData.length) {
                showSelectedByGroup(false, selectableData, checkedItems, tab);
            }
        }
        setCheckedItems((prevState) => {
            const newState = new Set(prevState);
            selectableData.forEach((item) => {
                const id = isAllOrgContext() ? item.orgId : item.id;
                if (isClear) {
                    newState.delete(id);
                    if (objRef[tab]?.current) {
                        objRef[tab].current = objRef[tab].current.filter((record) => {
                            if (isAllOrgContext()) {
                                return record.orgId !== item.orgId;
                            }
                            return record.id !== item.id;
                        });
                    }
                }
                else {
                    newState.add(id);
                    if (objRef[tab]?.current) {
                        const isExists = objRef[tab].current.some((record) => {
                            if (isAllOrgContext()) {
                                return record.orgId === item.orgId;
                            }
                            return record.id === item.id;
                        });
                        if (!isExists) {
                            objRef[tab].current.push(item);
                        }
                    }
                    if (objRef[tab] && !objRef[tab].current) {
                        objRef[tab].current = [item];
                    }
                }
            });
            return newState;
        });
    };
    const renderFormContent = (index) => {
        const usr = userData?.getUserDetailById[0];
        return (React.createElement(React.Fragment, null,
            React.createElement(Typography, { variant: "content-label-subhead", overrideTag: "div", className: styles.descContentTitle }, t(isEditMode
                ? TranslationsKeys.TAB_EDIT_CREDENTIALS
                : TranslationsKeys.TAB_CREATE_CREDENTIALS)),
            React.createElement("div", null,
                React.createElement(Typography, { variant: "p", className: styles.formFieldLabelBottomMargin, overrideTag: "div", color: (hasInputError.isNameRequired ||
                        hasInputError.isNameLong) &&
                        Colors.failure }, getLabelWithOrWithOutErrorIcon(styles.labelErrorWithIcon, hasInputError.isNameRequired ||
                    hasInputError.isNameLong, getInputLabel(hasInputError, t, false))),
                React.createElement(Input.Text, { ariaLabel: t(TranslationsKeys.FIELDS_GROUP_NAME_DESC), value: form.name, hasError: hasInputError.isNameRequired ||
                        hasInputError.isNameLong, id: FORM_INPUT_ITEM.name, "data-testid": FORM_INPUT_ITEM.name, className: cx(styles.formFieldBottomMargin, styles.formFieldBottomPadding), onChange: handleInputChange, disabled: isFieldDisabledForMultipleOrgs(currentOrgId, usr) ||
                        (isEditMode && isOktaEnabled() && isUserFederated()) }),
                React.createElement(Typography, { variant: "p", className: styles.formFieldLabelBottomMargin, overrideTag: "div", color: (hasInputError.isDisplayNameRequired ||
                        hasInputError.isDisplayNameLong) &&
                        Colors.failure }, getLabelWithOrWithOutErrorIcon(styles.labelErrorWithIcon, hasInputError.isDisplayNameRequired ||
                    hasInputError.isDisplayNameLong, getInputLabel(hasInputError, t, true))),
                React.createElement(Input.Text, { ariaLabel: t(TranslationsKeys.DISPLAY_NAME), value: form.displayName, hasError: hasInputError.isDisplayNameRequired ||
                        hasInputError.isDisplayNameLong, id: FORM_INPUT_ITEM.displayName, "data-testid": FORM_INPUT_ITEM.displayName, className: cx(styles.formFieldBottomMargin, styles.formFieldBottomPadding), onChange: handleInputChange, disabled: isFieldDisabledForMultipleOrgs(currentOrgId, usr) ||
                        (isEditMode && isOktaEnabled() && isUserFederated()) }),
                React.createElement(Checkbox, { id: FORM_INPUT_ITEM.allowOthersToShare, label: t(TranslationsKeys.FIELDS_ALLOW_OTHERS_TO_SHARE), checked: form.allowOthersToShare, onChange: handleCheckboxChange, className: styles.formFieldBottomPadding, disabled: isFieldDisabledForMultipleOrgs(currentOrgId, usr), showTooltipOnEllipsis: true, tooltipContent: t(TranslationsKeys.FIELDS_ALLOW_OTHERS_TO_SHARE) }),
                !isOktaEnabled() ? (React.createElement(React.Fragment, null,
                    React.createElement(Typography, { variant: "p", className: styles.formFieldLabelBottomMargin, overrideTag: "div", color: (hasInputError.isPasswordEmpty ||
                            hasInputError.isPasswordValidationRuleFailed) &&
                            Colors.failure }, getLabelWithOrWithOutErrorIcon(styles.labelErrorWithIcon, hasInputError.isPasswordEmpty ||
                        hasInputError.isPasswordValidationRuleFailed, getPasswordInputLabel(hasInputError, t, false))),
                    React.createElement(Input.Text, { type: passwordConfig.password
                            ? 'text'
                            : 'password', ariaLabel: t(TranslationsKeys.PASSWORD), value: form.password, hasError: hasInputError.isPasswordEmpty ||
                            hasInputError.isPasswordValidationRuleFailed, id: FORM_INPUT_ITEM.password, "data-testid": FORM_INPUT_ITEM.password, className: cx(styles.formFieldBottomMargin, styles.formFieldBottomPadding), onChange: handleInputChange, disabled: isFieldDisabledForMultipleOrgs(currentOrgId, usr), suffixIcon: passwordConfig.password
                            ? IconID.EYE
                            : IconID.EYE_UNDO, onSuffixIconClick: onChangePasswordSuffixIconClick }),
                    React.createElement(Typography, { variant: "p", className: styles.formFieldLabelBottomMargin, overrideTag: "div" }, t(TranslationsKeys.PASSWORD_REQUIREMENTS_TITLE)),
                    React.createElement(Horizontal, { className: styles.formFieldBottomPadding },
                        React.createElement(PasswordRequirements, null)),
                    React.createElement(Typography, { variant: "p", className: styles.formFieldLabelBottomMargin, overrideTag: "div", color: (hasInputError.isConfirmPasswordEmpty ||
                            hasInputError.arePasswordsNotMatching) &&
                            Colors.failure }, getLabelWithOrWithOutErrorIcon(styles.labelErrorWithIcon, hasInputError.isConfirmPasswordEmpty ||
                        hasInputError.arePasswordsNotMatching, getPasswordInputLabel(hasInputError, t, true))),
                    React.createElement(Input.Text, { type: passwordConfig.confirmPassword
                            ? 'text'
                            : 'password', ariaLabel: t(TranslationsKeys.CONFIRM_PASSWORD), value: form.confirmPassword, hasError: hasInputError.arePasswordsNotMatching, id: FORM_INPUT_ITEM.confirmPassword, "data-testid": FORM_INPUT_ITEM.confirmPassword, className: cx(styles.formFieldBottomMargin, styles.formFieldBottomPadding), onChange: handleInputChange, disabled: isFieldDisabledForMultipleOrgs(currentOrgId, usr), suffixIcon: passwordConfig.confirmPassword
                            ? IconID.EYE
                            : IconID.EYE_UNDO, onSuffixIconClick: onConfirmPasswordSuffixIconClick }))) : null,
                React.createElement(Typography, { variant: "p", className: styles.formFieldLabelBottomMargin, overrideTag: "div", color: (hasInputError.isNotWhitelistedDomainEmail ||
                        hasInputError.isEmailEmpty) &&
                        Colors.failure }, getLabelWithOrWithOutErrorIcon(styles.labelErrorWithIcon, hasInputError.isNotWhitelistedDomainEmail ||
                    hasInputError.isEmailEmpty, getEmailInputLabel(hasInputError, t), getWhitelistedDomainsText(hasInputError))),
                React.createElement(Input.Text, { type: "email", ariaLabel: t(TranslationsKeys.EMAIL), value: form.email, hasError: hasInputError.isNotWhitelistedDomainEmail ||
                        hasInputError.isEmailEmpty, id: FORM_INPUT_ITEM.email, "data-testid": FORM_INPUT_ITEM.email, onChange: handleInputChange, disabled: isFieldDisabledForMultipleOrgs(currentOrgId, usr) }))));
    };
    const onEmailSendButtonClick = () => {
        setEmailContent({
            ...emailContent,
            sendEmail: false,
        });
        const { id } = userData?.getUserDetailById[0].header;
        onSendWelcomeEmail(id, emailContent.message);
    };
    const onTestEmailSendButtonClick = () => {
        if (isTestEmailLabelEnabled && emailContent.testSendEmail) {
            setEmailContent({
                ...emailContent,
                testSendEmail: false,
            });
            const { id } = userData?.getUserDetailById[0].header;
            onSendWelcomeEmail(id, '');
        }
    };
    const renderCheckBoxesList = (index, selectedCountLabel, listData, originalListData, setCheckedItems, checkedItems, handleChange, readOnlyPredicate, isSearchIncluded = false, setSearchTerm, isListLoading = true) => {
        const isSelectAllCheckboxChecked = listData.length > 0 &&
            listData.every((data) => {
                if (isAllOrgContext()) {
                    return checkedItems.has(data.orgId);
                }
                return checkedItems.has(data.id);
            });
        return (React.createElement(React.Fragment, null,
            React.createElement(Vertical, { spacing: "a", className: styles.checkboxListTopContainer },
                React.createElement(Horizontal, { className: styles.contentHeading }, getTranslations(`users.modalTabLinkLabel.${createEditTabLinkLabels(isEditMode, currentOrgId)[index]}`)),
                isSearchIncluded && (React.createElement(SearchBar, { searchValue: "", searchPlaceholder: t('cirrus.metadata.search.placeHolder'), onSearchChange: (searchTerm) => {
                        setSearchTerm(searchTerm);
                    }, "data-testid": "search-bar-filters", className: styles.checkboxContainerSearchBox })),
                React.createElement(Horizontal, { className: styles.countFilterContainer },
                    React.createElement(Horizontal, { "data-testid": "orgsCountOrError" }, isOrgNotAssociated ? (React.createElement(Typography, { noMargin: true, variant: "p", overrideTag: "div", color: Colors.failure }, t(TranslationsKeys.ORGS_NOT_SELECTED))) : (`${t(TranslationsKeys[selectedCountLabel])} (${getSelectedCount(checkedItems, selectedCountLabel)})`)),
                    React.createElement(Horizontal, { className: styles.orgsActionBar },
                        React.createElement(Horizontal, { hAlignContent: "right" },
                            React.createElement(Typography, { variant: "p", overrideTag: "div", color: Colors.accent, className: styles.headerActionLink, onClick: () => {
                                    selectAllOrClearByGroup(originalListData, false, setCheckedItems, checkedItems, createEditTabLinkLabels(isEditMode, currentOrgId)[index]);
                                } }, t(TranslationsKeys.SELECT_ALL)),
                            React.createElement(Divider, { vertical: true, className: styles.headerActionLinkDivider }),
                            React.createElement(Typography, { variant: "p", overrideTag: "div", color: Colors.accent, className: styles.headerActionLink, onClick: () => {
                                    selectAllOrClearByGroup(originalListData, true, setCheckedItems, checkedItems, createEditTabLinkLabels(isEditMode, currentOrgId)[index]);
                                } }, t(TranslationsKeys.CLEAR)))))),
            React.createElement(Vertical, { className: styles.checkboxListContainer },
                React.createElement(LoadingIndicator.Global, { hideLoader: !isListLoading },
                    React.createElement(Vertical, { className: styles.checkboxList }, listData.map((item) => {
                        if (isAllOrgContext()) {
                            return (React.createElement(Checkbox, { id: item.orgId, key: item.orgId, label: item.orgName, checked: checkedItems.has(item.orgId), isList: true, onChange: e => {
                                    if (isOrgNotAssociated &&
                                        isAllOrgContext()) {
                                        setIsOrgNotAssociated(false);
                                    }
                                    if (item?.hasWarningMessageToShow &&
                                        e.target
                                            .checked) {
                                        setWarningDialogue({
                                            isOpen: true,
                                            title: item?.warningTitle,
                                            description: item?.warningDescription,
                                        });
                                    }
                                    handleChange(e, item);
                                }, disabled: readOnlyPredicate &&
                                    readOnlyPredicate(String(item.orgId)), showTooltipOnEllipsis: true, tooltipContent: item.orgName }));
                        }
                        return (React.createElement(Checkbox, { id: item.id, key: item.id, label: item.name, checked: checkedItems.has(item.id), isList: true, onChange: e => {
                                if (item?.hasWarningMessageToShow &&
                                    e.target
                                        .checked) {
                                    setWarningDialogue({
                                        isOpen: true,
                                        title: item?.warningTitle,
                                        description: item?.warningDescription,
                                    });
                                }
                                handleChange(e, item);
                            }, disabled: readOnlyPredicate &&
                                readOnlyPredicate(item.id), showTooltipOnEllipsis: true, tooltipContent: item.name }));
                    }))),
                React.createElement(Vertical, { className: styles.checkboxListShowSelectedContainer },
                    React.createElement(Toggle, { checked: isSelectAllCheckboxChecked, onChange: (e) => {
                            showSelectedByGroup(e.target.checked, originalListData, checkedItems, createEditTabLinkLabels(isEditMode, currentOrgId)[index]);
                        }, labelPosition: "right", "data-testid": `showSelected-${createEditTabLinkLabels(isEditMode, currentOrgId)[index]}`, label: t(TranslationsKeys.SHOW_SELECTED) })))));
    };
    const renderSendEmailContent = (index) => {
        if (isEditMode) {
            return (React.createElement(React.Fragment, null,
                React.createElement(Horizontal, { spacing: "c", className: styles.checkboxListTopContainer },
                    React.createElement(Typography, { variant: "content-label-subhead", overrideTag: "div", className: styles.descContentTitle }, getTranslations(`users.modalTabLinkLabel.${createEditTabLinkLabels(isEditMode, currentOrgId)[index]}`))),
                React.createElement("div", null,
                    emailContent.sendEmail ? (React.createElement(React.Fragment, null,
                        React.createElement(TextArea, { ariaLabel: "resend welcome email", value: emailContent.message, id: "sendWelcomeEmail", "data-testid": "sendWelcomeEmail", className: styles.formFieldBottomPadding, onChange: handleInputChange, placeholder: t(TranslationsKeys.SEND_MESSAGE_PLACEHOLDER) }),
                        React.createElement(Button, { text: t(TranslationsKeys.SEND), "data-testid": "edit-email-send-btn", onClick: onEmailSendButtonClick }))) : (React.createElement(Typography, { variant: "p" }, t(TranslationsKeys.EMAIL_RESEND_CONFIRMATION))),
                    isWelcomeEmailEnabled() &&
                        emailContent.testSendEmail ? (React.createElement(Horizontal, { className: styles.testWelcomeEmailCls },
                        React.createElement(Typography, { variant: "p", overrideTag: "div", color: isTestEmailLabelEnabled
                                ? Colors.accent
                                : Colors.base, className: styles.testEmailHeaderActionLink, onClick: onTestEmailSendButtonClick }, t(TranslationsKeys.TEST_WELCOME_EMAIL)))) : null,
                    !emailContent.testSendEmail ? (React.createElement(Typography, { variant: "p" }, t(TranslationsKeys.ADMIN_EMAIL_SENT_CONFIRMATION))) : null)));
        }
        return (React.createElement(React.Fragment, null,
            React.createElement(Horizontal, { spacing: "c", className: styles.checkboxListTopContainer },
                React.createElement(Typography, { variant: "content-label-subhead", overrideTag: "div", className: styles.descContentTitle }, getTranslations(`users.modalTabLinkLabel.${createEditTabLinkLabels(isEditMode, currentOrgId)[index]}`))),
            React.createElement(Horizontal, { spacing: "c", className: styles.checkboxListTopContainer },
                React.createElement(Checkbox, { id: "sendWelcomeEmailCheckbox", label: t(TranslationsKeys.SEND_EMAIL_LABEL), checked: sendWelcomeEmail, onChange: handleCheckboxChange })),
            React.createElement("div", null,
                React.createElement(TextArea, { ariaLabel: "send welcome email", value: emailContent.message, id: "sendWelcomeEmail", "data-testid": "sendWelcomeEmail", className: styles.formFieldBottomPadding, onChange: handleInputChange, placeholder: t(TranslationsKeys.SEND_MESSAGE_PLACEHOLDER), disabled: !sendWelcomeEmail }))));
    };
    const renderTabBody = (tabs, index) => {
        switch (tabs) {
            case createEditTabLinkLabels(isEditMode, currentOrgId)[0]:
                return renderFormContent(index);
            case createEditTabLinkLabels(isEditMode, currentOrgId)[1]:
                if (isAllOrgContext()) {
                    return renderCheckBoxesList(index, COUNT_LABELS.SELECTED_ORGS, filteredOrgList, orgList, setCheckedOrgItems, checkedOrgItems, (e, item) => {
                        const isChecked = e.target
                            .checked;
                        if (isChecked && selectedOrgsRef.current) {
                            selectedOrgsRef.current.push(item);
                        }
                        if (isChecked &&
                            !isEditMode &&
                            !selectedOrgsRef.current) {
                            selectedOrgsRef.current = [item];
                        }
                        if (!isChecked && selectedOrgsRef.current) {
                            selectedOrgsRef.current = selectedOrgsRef.current.filter((record) => record.orgId !== item.orgId);
                        }
                        handleOrgChange(e, item.orgId);
                    }, isCurrentUser
                        ? (orgId) => {
                            if (orgId === '0') {
                                if (isEditMode &&
                                    !ignoredSelections.current.has(orgId)) {
                                    ignoredSelections.current.add(orgId);
                                }
                                return true;
                            }
                            return false;
                        }
                        : null, true, (text) => {
                        setOrgsListInput(text);
                    }, isOrgListLoading);
                }
                return renderCheckBoxesList(index, COUNT_LABELS.SELECTED_GROUPS, filteredUserGroupList, userGroupList, setCheckedGroupItems, checkedGroupItems, (e, item) => {
                    const isChecked = e.target
                        .checked;
                    if (isChecked && selectedGroupsRef.current) {
                        selectedGroupsRef.current.push(item);
                    }
                    if (isChecked &&
                        !isEditMode &&
                        !selectedGroupsRef.current) {
                        selectedGroupsRef.current = [item];
                    }
                    if (!isChecked && selectedGroupsRef.current) {
                        selectedGroupsRef.current = selectedGroupsRef.current.filter((record) => record.id !== item.id);
                    }
                    handleGroupChange(e, item.id);
                }, (id) => {
                    const usr = userData?.getUserDetailById[0];
                    if (id !== getAdminGroupGuid()) {
                        return false;
                    }
                    const isCurrentUser = usr?.id === getUserGuid();
                    if (usr?.isSystemPrincipal ||
                        hasImplicitAdminAccess(usr?.isSuperUser, isCurrentUser)) {
                        if (isEditMode &&
                            !ignoredSelections.current.has(id)) {
                            ignoredSelections.current.add(id);
                        }
                        return true;
                    }
                    return false;
                }, true, (text) => {
                    setUserGroupsListInput(text);
                }, areUserAndGroupsLoading);
            case createEditTabLinkLabels(isEditMode, currentOrgId)[2]:
                if (isOktaEnabled())
                    return React.createElement(React.Fragment, null);
                return renderSendEmailContent(index);
            default:
                return React.createElement(React.Fragment, null);
        }
    };
    const renderTabContent = (ref, index) => {
        return (React.createElement("div", { ref: ref, className: cx(styles.rightPanelItem, {
                [styles.rightPanelItemNonFirst]: index !== 0,
            }), id: `section-${createEditTabLinkLabels(isEditMode, currentOrgId)[index]}` }, renderTabBody(createEditTabLinkLabels(isEditMode, currentOrgId)[index], index)));
    };
    generateRefs();
    return (React.createElement(Modal, { ...modalProps },
        React.createElement(Vertical, { className: styles.modalContent, hAlignContent: "stretch", grow: true, "data-testid": "user-create-edit-container-modal" },
            React.createElement(LoadingIndicator.Global, { hideLoader: !isLoading, "data-testid": "loading-indicator" },
                React.createElement(Alert.Banner, { message: errorMessage, isVisible: isError, type: BannerAlertType.ERROR, className: styles.alertBanner, onClose: onBannerClose }),
                React.createElement(Vertical, { "data-testid": "userModal_createEdit" },
                    React.createElement(Horizontal, { className: styles.mainContent },
                        React.createElement(View, { hAlignContent: "left", className: styles.viewContent },
                            renderTabLinks(componentRefs.current, createEditTabLinkLabels(isEditMode, currentOrgId)),
                            React.createElement(Vertical, { className: styles.rightPanel }, componentRefs.current.map((ref, index) => (React.createElement(Fragment, { key: `componentRefs_${createEditTabLinkLabels(isEditMode, currentOrgId)[index]}` },
                                renderTabContent(ref, index),
                                index !==
                                    componentRefs.current.length -
                                        1 && (React.createElement(Divider, { className: styles.rightPanelDivider }))))))))),
                React.createElement(Alert.Dialogue, { ...warningDialogue, primaryButton: {
                        text: t(TranslationsKeys.MODAL_OK),
                        onConfirm: onConfirmWarningDialogue,
                    }, onDismiss: onConfirmWarningDialogue })))));
};
//# sourceMappingURL=create-edit-form.container.js.map